import type { BinaryGrid } from "./symbolGrid";

export interface StrokeStyleParams {
    styleProfile: "neutral" | "bengali-like" | "cjk-like";
    curvature: number;
    cornerStyle: "sharp" | "rounded" | "bevel";
    cornerAmount: number;
}

export interface Point {
    x: number;
    y: number;
}

export interface SymbolLayoutOptions {
    rowWeights?: number[];
    colWeights?: number[];
    displacementStrength?: number;
}

export function buildSymbolPathD(
    grid: BinaryGrid,
    width: number,
    height: number,
    style: StrokeStyleParams,
    maxPathLength = 0,
    pathGapCells = 0,
    layout?: SymbolLayoutOptions,
): string {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    if (rows === 0 || cols === 0) return "";

    const points = buildTracePoints(grid, width, height, layout);
    if (points.length < 2) return "";

    return pointsToSmoothPath(points, style, maxPathLength, pathGapCells);
}

export function buildSymbolPathDs(
    grid: BinaryGrid,
    width: number,
    height: number,
    style: StrokeStyleParams,
    maxPathLength = 0,
    pathGapCells = 0,
    layout?: SymbolLayoutOptions,
): string[] {
    const components = getActiveComponents(grid);
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    if (rows === 0 || cols === 0) return [];
    const xCenters = computeCenters(width, cols, layout?.colWeights, layout?.displacementStrength);
    const yCenters = computeCenters(height, rows, layout?.rowWeights, layout?.displacementStrength);

    const paths: string[] = [];
    for (const component of components) {
        if (component.length < 2) continue;
        const points = component.map(([r, c]) => ({
            x: xCenters[c] ?? 0,
            y: yCenters[r] ?? 0,
        }));
        const ordered = orderByNearest(points);
        const path = pointsToSmoothPath(
            ordered,
            style,
            maxPathLength,
            pathGapCells,
        );
        if (path) paths.push(path);
    }
    return paths;
}

export function buildSymbolDotPoints(
    grid: BinaryGrid,
    width: number,
    height: number,
    layout?: SymbolLayoutOptions,
): Point[] {
    const components = getActiveComponents(grid);
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    if (rows === 0 || cols === 0) return [];
    const xCenters = computeCenters(width, cols, layout?.colWeights, layout?.displacementStrength);
    const yCenters = computeCenters(height, rows, layout?.rowWeights, layout?.displacementStrength);

    const dots: Point[] = [];
    for (const component of components) {
        if (component.length !== 1) continue;
        const [r, c] = component[0] as [number, number];
        dots.push({
            x: xCenters[c] ?? 0,
            y: yCenters[r] ?? 0,
        });
    }
    return dots;
}

function buildTracePoints(
    grid: BinaryGrid,
    width: number,
    height: number,
    layout?: SymbolLayoutOptions,
): Point[] {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    const xCenters = computeCenters(width, cols, layout?.colWeights, layout?.displacementStrength);
    const yCenters = computeCenters(height, rows, layout?.rowWeights, layout?.displacementStrength);
    const points: Point[] = [];

    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
            if (grid[r][c] === 0) continue;
            points.push({
                x: xCenters[c] ?? 0,
                y: yCenters[r] ?? 0,
            });
        }
    }

    return orderByNearest(points);
}

function computeCenters(
    span: number,
    count: number,
    weights: number[] | undefined,
    displacementStrength: number | undefined,
): number[] {
    const normalized = normalizeWeights(weights, count);
    const strength = Math.max(0, Math.min(1, displacementStrength ?? 1));
    const equal = 1 / Math.max(1, count);
    const blended = normalized.map((w) => equal + (w - equal) * strength);
    const total = blended.reduce((sum, w) => sum + w, 0) || 1;
    const centers: number[] = [];
    let cursor = 0;
    for (let i = 0; i < count; i += 1) {
        const size = (blended[i] / total) * span;
        centers.push(cursor + size * 0.5);
        cursor += size;
    }
    return centers;
}

function normalizeWeights(weights: number[] | undefined, count: number): number[] {
    if (!weights || weights.length !== count) {
        return Array.from({ length: count }, () => 1 / Math.max(1, count));
    }
    const cleaned = weights.map((w) => (Number.isFinite(w) && w > 0 ? w : 0));
    const sum = cleaned.reduce((acc, w) => acc + w, 0);
    if (sum <= 0) {
        return Array.from({ length: count }, () => 1 / Math.max(1, count));
    }
    return cleaned.map((w) => w / sum);
}

function pointsToSmoothPath(
    points: Point[],
    style: StrokeStyleParams,
    maxPathLength = 0,
    pathGapCells = 0,
): string {
    if (points.length < 2) return "";
    const segments = splitPointsByLength(points, maxPathLength, pathGapCells);
    const rendered = segments
        .map((segment) => renderSegmentPath(segment, style))
        .filter((d) => d.length > 0);
    return rendered.join(" ");
}

function splitPointsByLength(
    points: Point[],
    maxPathLength = 0,
    pathGapCells = 0,
): Point[][] {
    const cap = Math.max(0, Math.floor(maxPathLength));
    const gap = Math.max(0, Math.floor(pathGapCells));
    if (cap <= 0) return [points];

    const segments: Point[][] = [];
    let idx = 0;
    while (idx < points.length) {
        const endExclusive = Math.min(points.length, idx + cap + 1);
        const segment = points.slice(idx, endExclusive);
        if (segment.length >= 2) segments.push(segment);
        idx = endExclusive + gap;
    }
    return segments;
}

function renderSegmentPath(points: Point[], style: StrokeStyleParams): string {
    if (points.length < 2) return "";
    const profileCurvatureBias =
        style.styleProfile === "bengali-like"
            ? 0.75
            : style.styleProfile === "cjk-like"
              ? 0.25
              : 0.5;
    const smoothFactor = Math.max(
        0.1,
        Math.min(0.95, profileCurvatureBias * 0.7 + style.curvature * 0.3),
    );

    const cornerT = Math.max(0, Math.min(1, style.cornerAmount));
    const first = points[0] as Point;
    let d = `M ${first.x.toFixed(2)} ${first.y.toFixed(2)}`;

    if (style.cornerStyle === "sharp") {
        for (let i = 1; i < points.length; i += 1) {
            const curr = points[i] as Point;
            d += ` L ${curr.x.toFixed(2)} ${curr.y.toFixed(2)}`;
        }
    } else {
        for (let i = 1; i < points.length; i += 1) {
            const prev = points[i - 1] as Point;
            const curr = points[i] as Point;
            const next = points[i + 1] as Point | undefined;
            if (!next || cornerT <= 0) {
                d += ` L ${curr.x.toFixed(2)} ${curr.y.toFixed(2)}`;
                continue;
            }

            const inDx = curr.x - prev.x;
            const inDy = curr.y - prev.y;
            const outDx = next.x - curr.x;
            const outDy = next.y - curr.y;
            const inLen = Math.hypot(inDx, inDy);
            const outLen = Math.hypot(outDx, outDy);
            if (inLen < 1e-6 || outLen < 1e-6) {
                d += ` L ${curr.x.toFixed(2)} ${curr.y.toFixed(2)}`;
                continue;
            }

            const radius = Math.min(inLen, outLen) * 0.5 * cornerT;
            const p1x = curr.x - (inDx / inLen) * radius;
            const p1y = curr.y - (inDy / inLen) * radius;
            const p2x = curr.x + (outDx / outLen) * radius;
            const p2y = curr.y + (outDy / outLen) * radius;
            d += ` L ${p1x.toFixed(2)} ${p1y.toFixed(2)}`;
            if (style.cornerStyle === "bevel") {
                d += ` L ${p2x.toFixed(2)} ${p2y.toFixed(2)}`;
            } else {
                const cx = p1x + (curr.x - p1x) * smoothFactor;
                const cy = p1y + (curr.y - p1y) * smoothFactor;
                d += ` Q ${cx.toFixed(2)} ${cy.toFixed(2)}, ${p2x.toFixed(2)} ${p2y.toFixed(2)}`;
            }
        }
    }

    if (style.styleProfile === "bengali-like") {
        const last = points[points.length - 1] as Point;
        d += ` q 6 0 12 -4`;
        d += ` M ${(first.x - 8).toFixed(2)} ${(first.y - 12).toFixed(2)} h ${(Math.min(80, Math.abs(last.x - first.x) + 16)).toFixed(2)}`;
    }

    return d;
}

function distance(a: Point, b: Point): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
}

function orderByNearest(points: Point[]): Point[] {
    if (points.length <= 2) return points;
    const ordered: Point[] = [points[0] as Point];
    const remaining = points.slice(1);
    while (remaining.length > 0) {
        const prev = ordered[ordered.length - 1] as Point;
        let bestIndex = 0;
        let bestDistance = Number.POSITIVE_INFINITY;
        for (let i = 0; i < remaining.length; i += 1) {
            const dist = distance(prev, remaining[i] as Point);
            if (dist < bestDistance) {
                bestDistance = dist;
                bestIndex = i;
            }
        }
        ordered.push(remaining[bestIndex] as Point);
        remaining.splice(bestIndex, 1);
    }
    return ordered;
}

function getActiveComponents(grid: BinaryGrid): Array<Array<[number, number]>> {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    const visited = Array.from({ length: rows }, () => Array(cols).fill(false));
    const components: Array<Array<[number, number]>> = [];

    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
            if (grid[r][c] === 0 || visited[r][c]) continue;
            const queue: Array<[number, number]> = [[r, c]];
            const cells: Array<[number, number]> = [];
            visited[r][c] = true;
            while (queue.length > 0) {
                const [cr, cc] = queue.shift() as [number, number];
                cells.push([cr, cc]);
                const neighbors = [
                    [cr - 1, cc],
                    [cr + 1, cc],
                    [cr, cc - 1],
                    [cr, cc + 1],
                ];
                for (const [nr, nc] of neighbors) {
                    if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
                    if (visited[nr][nc] || grid[nr][nc] === 0) continue;
                    visited[nr][nc] = true;
                    queue.push([nr, nc]);
                }
            }
            components.push(cells);
        }
    }

    return components.sort((a, b) => b.length - a.length);
}
